/**
 * Booking Selector Component
 *
 * Calendar-based booking UI for scheduling appointments.
 * Shows multiple doctors' availability.
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Calendar, Check, ChevronLeft, ChevronRight, Clock, User } from 'lucide-react';
import { useMemo, useState } from 'react';

interface TimeSlot {
  time: string;
  available: boolean;
  patientName?: string; // If booked, shows patient name
}

interface Doctor {
  id: string;
  name: string;
  specialty: string;
  color: string;
}

interface BookingSelectorProps {
  suggestedDate?: string;
  suggestedTime?: string;
  onSubmit: (booking: {
    doctor: Doctor;
    date: string;
    time: string;
  }) => void;
  onClose?: () => void;
}

// Mock doctors
const DOCTORS: Doctor[] = [
  { id: 'me', name: 'Steindór Oddur Ellertsson', specialty: 'Sérnámslæknir', color: 'bg-primary' },
  { id: 'doc1', name: 'Anna Björk Jónsdóttir', specialty: 'Heimilislæknir', color: 'bg-blue-500' },
  { id: 'doc2', name: 'Ólafur Helgi Sigurðsson', specialty: 'Heimilislæknir', color: 'bg-green-500' },
  { id: 'doc3', name: 'Katrín Lilja Magnúsdóttir', specialty: 'Sérnámsgrunnslæknir', color: 'bg-purple-500' },
  { id: 'doc4', name: 'Einar Þór Guðmundsson', specialty: 'Heimilislæknir', color: 'bg-orange-500' },
];

// Mock patient names for booked appointments
const MOCK_PATIENTS = [
  'Jón Jónsson',
  'Guðrún Sigurðardóttir',
  'Kristján Ólafsson',
  'Helga Magnúsdóttir',
  'Sigurður Pétursson',
  'Anna Kristjánsdóttir',
  'Magnús Þórsson',
  'Birna Einarsdóttir',
  'Eiríkur Gunnarsson',
  'Þóra Björnsdóttir',
];

// Generate mock booked appointments for a doctor on a specific date
const getBookedAppointments = (doctorId: string, date: Date): Map<string, string> => {
  const booked = new Map<string, string>();
  const seed = doctorId.charCodeAt(0) + date.getDate() + date.getMonth();

  // Each doctor has different booked slots based on their ID and date
  const slots = [
    '08:40',
    '09:00',
    '09:20',
    '09:40',
    '10:00',
    '10:20',
    '10:40',
    '11:00',
    '11:20',
    '11:40',
    '13:00',
    '13:20',
    '13:40',
    '14:00',
    '14:20',
    '14:40',
  ];

  slots.forEach((slot, index) => {
    // Use pseudo-random to determine if slot is booked
    const isBooked = (seed + index * 7) % 3 === 0;
    if (isBooked) {
      const patientIndex = (seed + index) % MOCK_PATIENTS.length;
      booked.set(slot, MOCK_PATIENTS[patientIndex]);
    }
  });

  return booked;
};

// Check if today is weekend (Saturday=6, Sunday=0)
const getInitialWeekOffset = (): number => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6 ? 1 : 0;
};

// Generate time slots for a day (20-minute intervals)
// Morning: 8:40-12:00, Lunch: 12:00-13:00, Afternoon: 13:00-15:00
const generateTimeSlots = (doctorId: string, date: Date): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  const bookedAppointments = getBookedAppointments(doctorId, date);

  // Morning slots: 8:40, 9:00, 9:20, 9:40, 10:00, 10:20, 10:40, 11:00, 11:20, 11:40
  const morningSlots = [
    { hour: 8, min: 40 },
    { hour: 9, min: 0 },
    { hour: 9, min: 20 },
    { hour: 9, min: 40 },
    { hour: 10, min: 0 },
    { hour: 10, min: 20 },
    { hour: 10, min: 40 },
    { hour: 11, min: 0 },
    { hour: 11, min: 20 },
    { hour: 11, min: 40 },
  ];

  // Afternoon slots: 13:00, 13:20, 13:40, 14:00, 14:20, 14:40
  const afternoonSlots = [
    { hour: 13, min: 0 },
    { hour: 13, min: 20 },
    { hour: 13, min: 40 },
    { hour: 14, min: 0 },
    { hour: 14, min: 20 },
    { hour: 14, min: 40 },
  ];

  const allSlots = [...morningSlots, ...afternoonSlots];

  for (const { hour, min } of allSlots) {
    const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
    const patientName = bookedAppointments.get(time);
    const available = !patientName;
    slots.push({
      time,
      available,
      patientName,
    });
  }
  return slots;
};

// Icelandic weekday names
const WEEKDAYS_SHORT: Record<number, string> = {
  0: 'sun.',
  1: 'mán.',
  2: 'þri.',
  3: 'mið.',
  4: 'fim.',
  5: 'fös.',
  6: 'lau.',
};

const WEEKDAYS_LONG: Record<number, string> = {
  0: 'sunnudagur',
  1: 'mánudagur',
  2: 'þriðjudagur',
  3: 'miðvikudagur',
  4: 'fimmtudagur',
  5: 'föstudagur',
  6: 'laugardagur',
};

const MONTHS: Record<number, string> = {
  0: 'janúar',
  1: 'febrúar',
  2: 'mars',
  3: 'apríl',
  4: 'maí',
  5: 'júní',
  6: 'júlí',
  7: 'ágúst',
  8: 'september',
  9: 'október',
  10: 'nóvember',
  11: 'desember',
};

const MONTHS_SHORT: Record<number, string> = {
  0: 'jan.',
  1: 'feb.',
  2: 'mar.',
  3: 'apr.',
  4: 'maí',
  5: 'jún.',
  6: 'júl.',
  7: 'ágú.',
  8: 'sep.',
  9: 'okt.',
  10: 'nóv.',
  11: 'des.',
};

// Format date for week navigation display
const formatDate = (date: Date): string => {
  return `${WEEKDAYS_SHORT[date.getDay()]} ${date.getDate()}. ${MONTHS_SHORT[date.getMonth()]}`;
};

// Format date for selected date header
const formatDateLong = (date: Date): string => {
  return `${WEEKDAYS_LONG[date.getDay()]} ${date.getDate()}. ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

// Format date for booking summary
const formatDateMedium = (date: Date): string => {
  return `${WEEKDAYS_LONG[date.getDay()]} ${date.getDate()}. ${MONTHS[date.getMonth()]}`;
};

// Format date as YYYY-MM-DD
const toDateString = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

export function BookingSelector({ suggestedDate, suggestedTime, onSubmit, onClose }: BookingSelectorProps) {
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor>(DOCTORS[0]);
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    if (suggestedDate) {
      return new Date(suggestedDate);
    }
    // Default to next weekday
    const nextDay = new Date();
    nextDay.setDate(nextDay.getDate() + 1);
    // Skip weekend
    const dayOfWeek = nextDay.getDay();
    if (dayOfWeek === 0) nextDay.setDate(nextDay.getDate() + 1); // Sunday -> Monday
    if (dayOfWeek === 6) nextDay.setDate(nextDay.getDate() + 2); // Saturday -> Monday
    return nextDay;
  });
  const [selectedTime, setSelectedTime] = useState<string | null>(suggestedTime || null);
  const [weekOffset, setWeekOffset] = useState(getInitialWeekOffset);

  // Generate week days
  const weekDays = useMemo(() => {
    const days: Date[] = [];
    const startOfWeek = new Date();
    startOfWeek.setDate(startOfWeek.getDate() + weekOffset * 7);
    // Start from Monday
    const dayOfWeek = startOfWeek.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startOfWeek.setDate(startOfWeek.getDate() + diff);

    for (let i = 0; i < 5; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  }, [weekOffset]);

  // Get time slots for selected doctor and date
  const timeSlots = useMemo(() => {
    return generateTimeSlots(selectedDoctor.id, selectedDate);
  }, [selectedDoctor.id, selectedDate]);

  // Check if date is selected
  const isDateSelected = (date: Date): boolean => {
    return toDateString(date) === toDateString(selectedDate);
  };

  // Check if date is today
  const isToday = (date: Date): boolean => {
    return toDateString(date) === toDateString(new Date());
  };

  // Handle booking confirmation
  const handleConfirm = () => {
    if (selectedTime) {
      onSubmit({
        doctor: selectedDoctor,
        date: toDateString(selectedDate),
        time: selectedTime,
      });
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Doctor Selection */}
      <div className="mb-4">
        <label htmlFor="doctor-select" className="mb-2 block font-medium text-sm">
          Velja lækni
        </label>
        <Select
          value={selectedDoctor.id}
          onValueChange={(id) => {
            const doc = DOCTORS.find((d) => d.id === id);
            if (doc) setSelectedDoctor(doc);
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DOCTORS.map((doc) => (
              <SelectItem key={doc.id} value={doc.id}>
                <div className="flex items-center gap-2">
                  <div className={cn('h-2 w-2 rounded-full', doc.color)} />
                  <span>{doc.name}</span>
                  <span className="text-muted-foreground text-xs">({doc.specialty})</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Week Navigation */}
      <div className="mb-3 flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setWeekOffset((w) => w - 1)} disabled={weekOffset <= 0}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="font-medium text-sm">
          {weekDays[0] && formatDate(weekDays[0])} - {weekDays[4] && formatDate(weekDays[4])}
        </span>
        <Button variant="ghost" size="sm" onClick={() => setWeekOffset((w) => w + 1)} disabled={weekOffset >= 8}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Day Selection */}
      <div className="mb-4 grid grid-cols-5 gap-1">
        {weekDays.map((day) => (
          <Button
            key={toDateString(day)}
            variant={isDateSelected(day) ? 'default' : 'outline'}
            size="sm"
            className={cn('flex h-auto flex-col py-2', isToday(day) && !isDateSelected(day) && 'border-primary')}
            onClick={() => setSelectedDate(day)}
          >
            <span className="text-xs opacity-70">{WEEKDAYS_SHORT[day.getDay()]}</span>
            <span className="font-semibold text-lg">{day.getDate()}</span>
          </Button>
        ))}
      </div>

      {/* Selected Date Header */}
      <div className="mb-2 flex items-center gap-2">
        <Calendar className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">{formatDateLong(selectedDate)}</span>
      </div>

      {/* Time Slots */}
      <ScrollArea className="-mx-1 flex-1 px-1">
        <div className="flex flex-col gap-1 pb-4">
          {timeSlots.map((slot) => {
            const isSuggested = suggestedTime === slot.time;
            const isSelected = selectedTime === slot.time;
            const isBooked = !slot.available && slot.patientName;
            return (
              <div
                key={slot.time}
                role={slot.available ? 'button' : undefined}
                tabIndex={slot.available ? 0 : undefined}
                className={cn(
                  'relative flex items-center gap-2 rounded-md border p-2 transition-colors',
                  isBooked && 'border-slate-200 bg-slate-100',
                  slot.available && !isSelected && 'cursor-pointer border-slate-200 hover:bg-slate-50',
                  isSelected && 'border-green-600 bg-green-600 text-white',
                  isSuggested && !isSelected && slot.available && 'border-2 border-amber-400 bg-amber-50'
                )}
                onClick={() => slot.available && setSelectedTime(slot.time)}
                onKeyDown={(e) => e.key === 'Enter' && slot.available && setSelectedTime(slot.time)}
              >
                <div
                  className={cn(
                    'flex min-w-[60px] items-center gap-1 font-medium text-sm',
                    isSelected && 'text-white',
                    isBooked && 'text-slate-500'
                  )}
                >
                  <Clock className="h-3 w-3" />
                  {slot.time}
                </div>
                {isBooked ? (
                  <span className="flex-1 truncate text-slate-500 text-xs">{slot.patientName}</span>
                ) : (
                  <span className={cn('flex-1 text-xs', isSelected ? 'text-green-100' : 'text-green-600')}>Laust</span>
                )}
                {isSuggested && !isSelected && slot.available && (
                  <Badge variant="outline" className="border-amber-300 bg-amber-100 text-[10px] text-amber-700">
                    Stungið upp á
                  </Badge>
                )}
                {isSelected && <Check className="h-4 w-4 text-white" />}
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Selected Booking Summary */}
      {selectedTime && (
        <Card className="mb-4 border-green-200 bg-green-50 p-3">
          <div className="flex items-center gap-3">
            <div
              className={cn('flex h-10 w-10 items-center justify-center rounded-full text-white', selectedDoctor.color)}
            >
              <User className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm">{selectedDoctor.name}</div>
              <div className="text-muted-foreground text-xs">
                {formatDateMedium(selectedDate)} kl. {selectedTime}
              </div>
            </div>
            <Badge variant="outline" className="border-green-300 bg-green-100 text-green-700">
              Valið
            </Badge>
          </div>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-2 border-t pt-2">
        <Button variant="outline" className="flex-1" onClick={onClose}>
          Hætta við
        </Button>
        <Button className="flex-1 bg-green-600 hover:bg-green-700" disabled={!selectedTime} onClick={handleConfirm}>
          <Check className="mr-2 h-4 w-4" />
          Staðfesta tíma
        </Button>
      </div>
    </div>
  );
}
